import { useRef } from "react";
import { Mic, MicOff, Square, Loader2, Wand2 } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DialogFooter } from "../ui/dialog";
import { toast } from "sonner";

const VoiceMode = ({
  voiceTranscript,
  setVoiceTranscript,
  isRecordingVoice,
  setIsRecordingVoice,
  voiceGenre,
  setVoiceGenre,
  voiceTargetLength,
  setVoiceTargetLength,
  processingVoice,
  onTransformVoice,
  onBack,
}) => {
  const voiceRecognitionRef = useRef(null);

  const handleToggleRecording = () => {
    if (isRecordingVoice) {
      if (voiceRecognitionRef.current) {
        voiceRecognitionRef.current.stop();
      }
      setIsRecordingVoice(false);
    } else {
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.lang = 'en-US';
        
        recognition.onresult = (event) => {
          let newText = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
              newText += event.results[i][0].transcript + ' ';
            }
          }
          if (newText) {
            setVoiceTranscript(prev => (prev + newText).trim());
          }
        };
        
        recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          if (event.error !== 'no-speech') {
            toast.error(`Speech error: ${event.error}`);
          }
          setIsRecordingVoice(false);
        };
        
        recognition.onend = () => {
          setIsRecordingVoice(false);
        };
        
        voiceRecognitionRef.current = recognition;
        recognition.start();
        setIsRecordingVoice(true);
        toast.info("Start speaking your story idea...");
      } else {
        toast.error("Speech recognition not supported in this browser");
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-3 transition-all ${
          isRecordingVoice 
            ? 'bg-red-500/20 voice-recording' 
            : 'bg-purple-500/20'
        }`}>
          {isRecordingVoice ? (
            <MicOff className="w-10 h-10 text-red-500" />
          ) : (
            <Mic className="w-10 h-10 text-purple-500" />
          )}
        </div>
        <h3 className="font-semibold text-lg">Speak Your Story Idea</h3>
        <p className="text-sm text-muted-foreground">
          {isRecordingVoice 
            ? "I'm listening... Click to stop when done" 
            : "Click the microphone and tell me your story idea"}
        </p>
      </div>

      <div className="flex justify-center">
        <Button
          size="lg"
          className={`rounded-full w-32 h-32 btn-pop ${
            isRecordingVoice 
              ? 'bg-red-500 hover:bg-red-600 pulse-glow' 
              : 'bg-purple-500 hover:bg-purple-600'
          }`}
          onClick={handleToggleRecording}
          disabled={processingVoice}
          data-testid="voice-record-btn"
        >
          {isRecordingVoice ? (
            <Square className="w-12 h-12" />
          ) : (
            <Mic className="w-12 h-12" />
          )}
        </Button>
      </div>

      {voiceTranscript && (
        <div className="mt-4">
          <label className="block text-sm font-medium mb-2">Your Story Idea:</label>
          <Textarea
            value={voiceTranscript}
            onChange={(e) => setVoiceTranscript(e.target.value)}
            className="min-h-[120px] bg-secondary"
            placeholder="Your spoken words will appear here..."
            data-testid="voice-transcript"
          />
          <p className="text-xs text-muted-foreground mt-1">
            {voiceTranscript.split(' ').filter(w => w).length} words captured. Feel free to edit!
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Genre</label>
          <Select value={voiceGenre} onValueChange={setVoiceGenre}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="literary">Literary Fiction</SelectItem>
              <SelectItem value="fantasy">Fantasy</SelectItem>
              <SelectItem value="scifi">Science Fiction</SelectItem>
              <SelectItem value="romance">Romance</SelectItem>
              <SelectItem value="thriller">Thriller</SelectItem>
              <SelectItem value="horror">Horror</SelectItem>
              <SelectItem value="mystery">Mystery</SelectItem>
              <SelectItem value="childrens">Children's</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Output</label>
          <Select value={voiceTargetLength} onValueChange={setVoiceTargetLength}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="short">Short Story (1-2k words)</SelectItem>
              <SelectItem value="medium">Novella Outline (5-10k)</SelectItem>
              <SelectItem value="full">Novel Outline (15-25 chapters)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter className="flex gap-2 mt-6">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button 
          onClick={onTransformVoice}
          className="bg-purple-500 hover:bg-purple-600 btn-pop"
          disabled={!voiceTranscript || voiceTranscript.length < 20 || processingVoice}
          data-testid="voice-generate-btn"
        >
          {processingVoice ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Wand2 className="w-4 h-4 mr-2" />
              Transform into Story
            </>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default VoiceMode;
